import { Accordion } from 'react-bootstrap';
const startScreenText = (): JSX.Element => {
  return (
    <div>
      <div className="summary-body tool-front">
        <p>
          The Tobacco, Alcohol, Prescription medication, and other Substance use (TAPS) Tool
          consists of a combined screening component (TAPS-1) followed by a brief assessment
          (TAPS-2) for those who screen positive.
        </p>
        <p>This tool:</p>
        <ul>
          <li>
            Combines screening and brief assessment for commonly used substances, eliminating the
            need for multiple screening and lengthy assessment tools
          </li>
          <li>
            Provides a two stage brief assessment adapted from the NIDA quick screen and brief
            assessment (adapted ASSIST-lite)
          </li>
          <li>
            May be either self-administered directly by the patient or as an interview by a health
            professional
          </li>
          <li>Uses an electronic format (available here as an online tool)</li>
          <li>
            Uses a screening component to ask about frequency of substance use in the past 12 months
          </li>
          <li>Facilitates a brief assessment of past 3 months problem use to the patient</li>
        </ul>
      </div>

      <Accordion title="More Information About This Tool">
        <Accordion.Item eventKey="0">
          <Accordion.Header>More Information About This Tool</Accordion.Header>
          <Accordion.Body>
            <h5 className="extra-margin-top">
              TAPS : The Tobacco, Alcohol, Prescription medication, and other Substance use (TAPS)
              Tool
            </h5>

            <p>
              The TAPS Tool has two components. The first component (TAPS-1) is a 4-item screen for
              tobacco, alcohol, illicit drugs, and non-medical use of prescription drugs. If an
              individual screens positive on TAPS-1 (i.e., reports other than &ldquo;never&ldquo;),
              the tool will automatically begin the second component (TAPS-2), which consists of
              brief substance-specific assessment questions (TAPS-2) to arrive at a risk level for
              that substance. Clinicians are encouraged to provide positive feedback to patients who
              screen negative and support their choice to abstain from substances. For patients who
              have a positive screen on the TAPS-1, a brief assessment (TAPS-2) identifies the
              specific substance(s) use and risk level, ranging in severity from &ldquo;problem
              use&rdquo; to the more severe substance use disorder (SUD).
            </p>

            <h5>Instructions:</h5>

            <p>
              These tools can be used by the patient (self-administered) online or administered as
              an interview by a health professional. Upon completion, the tool will automatically
              generate a risk level for each substance class. Implications of the score, along with
              suggested clinician actions and additional resources, will be provided.
            </p>

            <h5>Screening Tool Cutoffs and Scoring Thresholds:</h5>

            <p>
              The TAPS tool starts with questions about the frequency of use of tobacco, alcohol,
              and other substances in the past 12 months. Endorsement of any substance use during
              the initial screening phase (TAPS-1) prompts few additional questions regarding
              use-related behaviors through a brief assessment (TAPS-2). Scores on these questions
              generate a risk level per substance endorsed, based on a range of possible scores per
              substance.
            </p>

            <table className="scoring-table bstad">
              <thead>
                <tr>
                  <th scope="col">TAPS Score</th>
                  <th scope="col" className="risk-category-column">
                    Risk Category
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>0</td>
                  <td>No Use in Past 3 Months</td>
                </tr>
                <tr>
                  <td>1</td>
                  <td>Problem Use</td>
                </tr>
                <tr>
                  <td>2+</td>
                  <td>Higher Risk</td>
                </tr>
              </tbody>
            </table>

            <p>
              For identifying DSM-5 SUD at the recommended cutoff of 2+, the TAPS Tool has adequate
              sensitivity (&gt;70%) only for tobacco, alcohol, and marijuana. Further assessment
              should be conducted for patients with a score of 1+ for other substances. This
              assessment is a high priority for patients with a TAPS score of 2+, given its high
              positive predictive value for most substance classes.
            </p>

            <h5 className="extra-padding-top">Tool Development and Validation:</h5>

            <p>
              The above text has extra references in it (the WHO CIDI SAM). The TAPS Tool was
              compared to a reference standard measure in adult primary care patients and
              demonstrated adequate psychometric properties.
            </p>

            <p>
              McNeely J, Wu L, Subramaniam G, Sharma G, Cathers LA, Svikis D, et al.
              <em>
                Performance of the Tobacco, Alcohol, Prescription Medication, and Other Substance
                Use (TAPS) Tool for Substance Use Screening in Primary Care Patients.
              </em>
              Ann Intern Med. 2016;165:690-699. doi: 10.7326/M16-0317
            </p>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="1">
          <Accordion.Header>Frequently Asked Questions About Screening</Accordion.Header>
          <Accordion.Body>
            <h5 className="extra-margin-top">Q: Who should use this tool?</h5>
            <p>
              A: This screening tool is meant to be used under a medical provider’s supervision. It
              is not intended to guide self-assessment, diagnosis, or treatment decision making. The
              TAPS tool provides information about risk categories to providers but additional
              assessments are recommended to confirm diagnosis of a substance use disorder.
            </p>

            <h5>Q: Why should I use validated tools?</h5>
            <p>
              A: Validated tools are important for an accurate understanding of a patient’s risk of
              substance use disorders and are more likely to correctly identify patients with
              substance-related problems and disorders.
            </p>
            <h5>Q: Is this tool appropriate for the primary care setting?</h5>
            <p>
              A: Yes. A recent study demonstrated that this tool was validated in primary care
              settings for identifying adults with and without problem use/SUDs. Appropriate
              substance use screening and brief assessment in primary care can
            </p>
            <ul>
              <li>Normalize discussions about substance use</li>
              <li>Provide opportunities for prevention by reinforcing healthy behaviors</li>
              <li>
                Assist providers in diagnosing and treating medical and psychiatric conditions
              </li>
              <li>Inform prescribing practices to avoid overdose and medication interactions</li>
              <li>Identify patients at risk for problem substance use</li>
              <li>Guide brief interventions and treatment recommendations</li>
              <li>Identify patients in need of treatment for a SUD</li>
            </ul>
            <h5>Q: How much time does the tool take to administer?</h5>
            <p>
              A: The tool is designed to be brief and should take fewer than 5 minutes to
              administer, but may be completed quicker if the patient does not endorse recent
              substance use.{' '}
            </p>

            <h5>Q: Is this provider-administered or patient-administered?</h5>
            <p>
              A: It has been found to be valid for both modes of administration, and you may choose
              an approach that best suits your practice flow and resources. Self-administration is
              recommended when possible, as it often results in better implementation.
            </p>

            <h5>Q: Should I share the results with my patient?</h5>
            <p>
              A: Yes. Sharing screening results is the start of a productive conversation with your
              patient about their substance use, and strategies to reduce their risk. The tool
              provides information on risk categories, and the results offer guidance to clinicians
              based on guidance derived from expert consensus.
            </p>

            <h5>Q: Where can I find additional tools for higher risk patients?</h5>
            <p>
              A: For patients with problem use or a higher risk of SUD, you may wish to use a
              clinicial interview for further assessment. The questions from the{' '}
              <a href="https://www.samhsa.gov/substance-use/prevention/substance-use-disorders">
                DSM-5
              </a>{' '}
              SUD criteria can be used as an interview guide. You might also conduct a brief
              intervention, using the{' '}
              <a href="https://public.health.oregon.gov/HealthyPeopleFamilies/ReproductiveSexualHealth/Documents/edmat/OARSEssentialCommunicationTechniques.pdf">
                OARS framework
              </a>{' '}
              (&ldquo;Open Questions,&rdquo; &ldquo;Affirmations,&rdquo; &ldquo;Reflective
              Listening,&rdquo; and &ldquo;Summarizing&rdquo;) and other motivational interviewing
              techniques, or a&nbsp;
              <a href="https://medicine.yale.edu/sbirt/">brief negotiated interview</a>.
            </p>
            <h5>Q: Can you tell me more about motivational interviewing?</h5>
            <p>
              A: Motivational interviewing is a conversational method for talking with your patients
              about several health issues, including substance use. It is a strategy, based on a
              high level of evidence, to help engage individuals in making a change in their
              behaviors. The following resources provide additional information:
            </p>
            <ul>
              <li>
                <a href="http://www.motivationalinterviewing.org">
                  {' Motivational Interviewing Network of Trainers (MINT) '}
                </a>
              </li>
              <li>
                <a href="https://www.nida.nih.gov/blending-initiative/cme-ce-simulation">
                  {' Blending Initiative Motivational Interviewing CME/CE and Patient Simulation '}
                </a>
              </li>
            </ul>
            <h5>Q: Where can I find more information about referral to specialty care?</h5>
            <p>
              A: Clinicians may wish to identify local providers and or specialty care SUD programs
              to which to refer patients with higher risk levels well before beginning systematic
              screening. This will greatly assist with finding a suitable referral when the need
              arises. The Substance Abuse and Mental Health Services Administration&apos;s Treatment
              Locater website provides information about publicly funded SUD treatment facilities:{' '}
              <br />
              <br />
              <a href="https://findtreatment.samhsa.gov/">{' FindTreatment.Gov '}</a>
            </p>
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
      <h4 className="summary-disclaimer">
        Intended use: This screening tool is meant to be used under a medical provider’s supervision
        and is not intended to guide self-assessment or take the place of a healthcare provider’s
        clinical judgment.
        <br />
        <br />
        This tool may be administered by either the patient or the clinician. Please indicate the
        mode of administration:
      </h4>
    </div>
  );
};

export default startScreenText;
